'use client'

import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import CalendlyModal from '@/components/ui/CalendlyModal'
import { menuContainsPath, type NavItem, type NavMenuItem } from '@/lib/nav'

type MobileMenuProps = {
  navItems: NavItem[]
}

/**
 * The overlay and panel are portalled to <body>, not left inside the header.
 * The header carries `backdrop-blur-md`, and a backdrop-filter makes an element
 * the containing block for its `fixed` descendants — in place, `fixed inset-0`
 * resolved against the 73px header strip rather than the viewport, so the
 * backdrop neither dimmed the page nor caught a tap outside the menu. The panel
 * only looked right because the header happens to sit at the top, full width.
 */
export default function MobileMenu({ navItems }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const pathname = usePathname()
  // createPortal needs a DOM target, so the panel only exists after mount. It
  // starts closed, so there's nothing to miss on the server pass.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Close on route change
  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  // Lock scroll when open
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <>
      <button
        type="button"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-10 w-10 items-center justify-center rounded-md text-fg-secondary transition-colors hover:text-fg lg:hidden"
      >
        <span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
        {isOpen ? (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M4 4L16 16M16 4L4 16"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M3 5H17M3 10H17M3 15H17"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
            />
          </svg>
        )}
      </button>

      {mounted &&
        createPortal(
          <>
            {/* Overlay */}
            <div
              aria-hidden="true"
              onClick={() => setIsOpen(false)}
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden',
                isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
            />

            {/* Slide-down panel */}
            <nav
              aria-label="Mobile navigation"
              className={cn(
                'fixed inset-x-0 top-[73px] z-40 border-b border-line bg-bg transition-all duration-300 lg:hidden',
                isOpen ? 'visible opacity-100' : 'invisible -translate-y-2 opacity-0'
              )}
            >
              <ul className="flex flex-col divide-y divide-line px-4">
                {navItems.map((item) => (
                  <li key={item.label}>
                    {item.kind === 'booking' ? (
                      <CalendlyModal
                        label={item.label}
                        size="md"
                        variant="secondary"
                        className="my-3 w-full"
                        onOpen={() => setIsOpen(false)}
                      />
                    ) : item.kind === 'menu' ? (
                      <MobileGroup
                        // Remount per route so a section opens when you are in it.
                        key={pathname}
                        menu={item}
                        pathname={pathname}
                        onNavigate={() => setIsOpen(false)}
                      />
                    ) : (
                      <Link
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          'flex w-full items-center py-4 text-sm font-medium transition-colors',
                          pathname === item.href ? 'text-fg' : 'text-fg-secondary hover:text-fg'
                        )}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          </>,
          document.body
        )}
    </>
  )
}

/** A dropdown's links as a nested disclosure, open by default inside its section. */
function MobileGroup({
  menu,
  pathname,
  onNavigate,
}: {
  menu: NavMenuItem
  pathname: string
  onNavigate: () => void
}) {
  const [open, setOpen] = useState(() => menuContainsPath(menu, pathname))
  const panelId = useId()
  const links = [
    ...menu.groups.flatMap((g) => [
      ...(g.heading ? [{ ...g.heading, child: false }] : []),
      ...g.links.map((l) => ({ ...l, child: Boolean(g.heading) })),
    ]),
    { ...menu.hub, child: false },
  ]

  return (
    <>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between py-4 text-sm font-medium text-fg-secondary transition-colors hover:text-fg"
      >
        {menu.label}
        <svg
          width="12"
          height="12"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
          className={cn('h-3 w-3 shrink-0 transition-transform duration-200', open && 'rotate-180')}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      <ul id={panelId} hidden={!open} className="space-y-1 pb-4 pl-4">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              onClick={onNavigate}
              aria-current={pathname === link.href ? 'page' : undefined}
              className={cn(
                'block py-2 text-sm transition-colors',
                link.child && 'pl-4',
                pathname === link.href ? 'text-fg' : 'text-fg-secondary hover:text-fg'
              )}
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </>
  )
}
